import { forwardRef } from "react";
import { Home, Search, MessageCircle, Wallet, User } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";

export const BottomNav = forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  (props, ref) => {
    const location = useLocation();
    const { user } = useAuth();
    const { t } = useLanguage();
    
    const navItems = [
      { icon: Home, label: t.bottomNav.home, href: "/" },
      { icon: Search, label: t.bottomNav.search, href: "/browse" },
      { icon: MessageCircle, label: t.bottomNav.chat, href: "/chat", badge: 3, requiresAuth: true },
      { icon: Wallet, label: t.bottomNav.wallet, href: "/wallet", requiresAuth: true },
      { icon: User, label: t.bottomNav.profile, href: user ? "/profile" : "/auth" },
    ];

    const isActive = (href: string) => location.pathname === href;

    return (
      <nav 
        ref={ref}
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card md:hidden"
        {...props}
      >
        <div className="flex items-center justify-around py-2">
          {navItems.map((item) => {
            // Skip auth-required items for non-logged-in users
            if (item.requiresAuth && !user) return null;
            
            return (
              <Link
                key={item.label}
                to={item.href}
                aria-label={item.label}
                aria-current={isActive(item.href) ? "page" : undefined}
                className={`relative flex flex-col items-center gap-1 px-3 py-2 ${
                  isActive(item.href)
                    ? "text-primary"
                    : "text-foreground/80"
                }`}
              >
                <div className="relative">
                  <item.icon className="h-5 w-5" aria-hidden="true" />
                  {item.badge && user && (
                    <span
                      aria-label={`${item.badge} unread`}
                      className="absolute -right-2 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground"
                    >
                      {item.badge}
                    </span>
                  )}
                </div>
                <span className="text-[11px] font-semibold leading-tight">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    );
  }
);

BottomNav.displayName = "BottomNav";